// material-ui
import {
    Autocomplete,
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography
} from "@mui/material";

// third party
import {useFormik} from "formik";
import * as yup from "yup";

// project imports
import AnimateButton from "ui-component/components/AnimateButton";
import {useDispatch, useSelector} from "store";
import {FormattedMessage, useIntl} from "react-intl";
import React, {useEffect, useState} from "react";
import {format} from "date-fns";
import {getAllStudents} from "../../store/actions/student.actions";
import {getAllTeachers} from "../../store/actions/teacher.actions";
import {addActivity, getLastConsolidate, updateActivity} from "../../store/actions/activites.actions";
import {LoadingButton} from "@mui/lab";
import {getAllServices} from "../../store/actions/service.actions";
import {getAllMembers} from "../../store/actions/member.actions";
import {User} from "../../types/type/users-types";
import LoadingSpinner from "../table/LoadingSpinner";
import {DateTimePicker, LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import it from "date-fns/locale/it"
import SuccessResult from "./SuccessResult";
import {ActivityType, Argument, LocationType, ServiceType, Topic} from "../../types/type/activities-types";
import {FirmType} from "../../types/type/financials-types";
import {openSnackbar} from "../../store/slices/snackbar";
import ModalAction from "../components/ModalAction";
import moment from "moment/moment";
import useConfig from "../../hooks/useConfig";
import Chip from "../components/Chip";
import useAuth from "../../hooks/useAuth";
import ModalWarningActivity from "./ModalWarningActivity";
import axiosDomain from "../../utils/axios/axiosDomain";
import {Simulate} from "react-dom/test-utils";
import load = Simulate.load;
import {useTheme} from "@mui/styles";

interface Props {
    handleDrawerChangeState: () => void;
    currentActivity?: ActivityType | undefined;
    startDate?: any | undefined;
    endDate?: any | undefined;
    seatId?: any | undefined;
    roomId?: any | undefined;
}

// ==============================|| ACTIVITY - FORM FOR EDIT/VIEW ||============================== //

const ActivityForm = ({handleDrawerChangeState, currentActivity, seatId, startDate, endDate, roomId}: Props) => {
    const dispatch = useDispatch();
    const intl = useIntl()
    const {user} = useAuth();
    const theme = useTheme();
    const {company, onChangeReloadPage, reloadPage} = useConfig()
    const minDateStart = moment(new Date()).subtract(10, "minute").toDate();
    const [teacherData, setTeacherData] = useState<User[]>([])
    const [studentData, setStudentData] = useState<User[]>([])
    const [firmData, setFirmData] = useState<FirmType[]>([])
    const [serviceData, setServiceData] = useState<ServiceType[]>([])
    const [argumentData, setArgumentData] = useState<Argument[]>([])
    const [topicData, setTopicData] = useState<Topic[]>([])
    const [locationData, setLocationData] = useState<LocationType[]>([])
    const [minDateEnd, setMinDateEnd] = useState(currentActivity ? moment(new Date(currentActivity.start_date)).subtract(10, "minute").toDate() : moment(new Date()).subtract(10, "minute").toDate())
    const [valueToUpdate, setValueToUpdate] = useState<ActivityType>()
    const [showSuccessResult, setSuccessResult] = useState(false)
    const [showWarningDialog, setWarningDialog] = useState(false)
    const [resultCheck, setResultCheck] = useState<any>()
    const [valueResultCheck, setResultValueCheck] = useState<any>()
    const [loading, setLoading] = useState(true)
    const {firm} = useSelector((state) => state.firm);
    const {member} = useSelector((state) => state.member);
    const {student} = useSelector((state) => state.student);
    const {location} = useSelector((state) => state.location);
    const {teacher} = useSelector((state) => state.teacher);
    const {service} = useSelector((state) => state.service);
    const {topic} = useSelector((state) => state.topic);
    const {argument} = useSelector((state) => state.argument);
    const {loadingOperation} = useSelector((state) => state.loading);

    useEffect(() => {
        const init = async () => {
            setLoading(true)
            await dispatch(getAllStudents());
            await dispatch(getAllServices());
            await dispatch(getAllTeachers());
            await dispatch(getAllMembers());
            setLoading(false)
        }
        init()
    }, [])

    useEffect(() => {
        if (company)
            formikRecharge.setFieldValue("location_id", company);
    }, [company])

    const [openModal, setOpenModal] = useState(false);
    const handleCloseModal = () => {
        setOpenModal(false)
    }

    const handleCloseWarningModal = () => {
        setWarningDialog(false)
    }

    const validationSchema = yup.object({
        affiliate_id: yup.string().required(intl.formatMessage({id: "FRM00077"})),
        student_id: yup.string().required(intl.formatMessage({id: "FRM00079"})),
        teacher_id: yup.string().required(intl.formatMessage({id: "FRM00080"})),
        service_id: yup.string().required(intl.formatMessage({id: "FRM00081"})),
        seat: yup.string(),
        firm_id: yup.string().required(intl.formatMessage({id: "FRM00085"})),
    });

    let initialValues = {
        affiliate_id: "",
        student_id: "",
        request_date: format(new Date(), "yyyy-MM-dd HH:mm"),
        activity_date: format(new Date(), "yyyy-MM-dd HH:mm"),
        start_date: format(new Date(), "yyyy-MM-dd HH:mm"),
        end_date: format(new Date(), "yyyy-MM-dd HH:mm"),
        firm_id: "",
        teacher_id: user?.type == "4" ? user.id : "",
        service_id: "",
        topic_id: "",
        argument_id: "",
        level_id: "",
        seat: "",
        room: "",
        act_status: 0,
        closed: "0",
        cost: "0",
        location_id: company
    }

    const formikRecharge = useFormik({
        enableReinitialize: false,
        validationSchema,
        initialValues: initialValues,
        onSubmit: async (values, {resetForm}) => {
            setLoading(true)
            const beforeActUrl = currentActivity ? "/beforeact/" + currentActivity.id : "/beforeact"
            const {data} = await axiosDomain.post(beforeActUrl, {
                ...values,
                end_date: format(moment(new Date(values.end_date)).subtract(10, "minutes").toDate(), "yyyy-MM-dd HH:mm"),
                start_date: format(moment(new Date(values.start_date)).add(10, "minutes").toDate(), "yyyy-MM-dd HH:mm"),
                status: 1,
                duration: moment(new Date(values.end_date)).diff(new Date(values.start_date), "minutes")
            });
            setLoading(false)
            if (data.result.teacher_is_busy || !data.result.platfond_is_enough || !data.result.teacher_ratio_is_ok) {
                setResultCheck(data)
                setWarningDialog(true)
                setResultValueCheck(values)
            } else {
                if (currentActivity) {
                    setValueToUpdate(values)
                    setOpenModal(true)
                } else {
                    await dispatch(addActivity(values));
                    setSuccessResult(true);
                    onChangeReloadPage(!reloadPage)
                    formikRecharge.resetForm({values: initialValues})
                }
            }
        }
    });
    const handleConfirmAnyway = async () => {
        if (currentActivity) {
            setValueToUpdate(valueResultCheck)
            setOpenModal(true)
        } else {
            await dispatch(addActivity(valueResultCheck));
            setSuccessResult(true);
            onChangeReloadPage(!reloadPage)
            formikRecharge.resetForm({values: initialValues})
        }
        setWarningDialog(false)
    }

    const handleConfirm = async () => {
        await dispatch(updateActivity(currentActivity?.id, valueToUpdate));
        dispatch(
            openSnackbar({
                open: true,
                message: intl.formatMessage({id: "CUW00019"}),
                variant: "alert",
                alert: {
                    color: "success"
                },
                close: false
            })
        );
        onChangeReloadPage(!reloadPage)
        await dispatch(getLastConsolidate(company));
        handleDrawerChangeState();
        handleCloseModal()
    }

    useEffect(() => {
        setTeacherData(teacher)
        if (currentActivity) {
            formikRecharge.setFieldValue("teacher_id", currentActivity?.teacher_id?.toString());
            formikRecharge.setFieldValue("start_date", format(new Date(currentActivity?.start_date), "yyyy-MM-dd HH:mm"));
            formikRecharge.setFieldValue("end_date", format(new Date(currentActivity?.end_date), "yyyy-MM-dd HH:mm"));
            formikRecharge.setFieldValue("activity_date", format(new Date(currentActivity?.start_date), "yyyy-MM-dd HH:mm"))
            formikRecharge.setFieldValue("request_date", format(new Date(currentActivity?.start_date), "yyyy-MM-dd HH:mm"))
        } else {
            if (startDate) {
                formikRecharge.setFieldValue("start_date", startDate);
                formikRecharge.setFieldValue("activity_date", startDate)
                formikRecharge.setFieldValue("request_date", startDate)
            }
            if (endDate) {
                formikRecharge.setFieldValue("end_date", endDate);
            }
        }
    }, [teacher])

    useEffect(() => {
        setStudentData(student)
        if (currentActivity) {
            formikRecharge.setFieldValue("student_id", currentActivity?.student_id?.toString());
            formikRecharge.setFieldValue("affiliate_id", currentActivity?.affiliate_id?.toString());
        }
    }, [student])

    useEffect(() => {
        setFirmData(firm)
        if (currentActivity) {
            formikRecharge.setFieldValue("firm_id", currentActivity?.firm_id?.toString());
        }
    }, [firm])

    useEffect(() => {
        setServiceData(service)
        if (currentActivity) {
            formikRecharge.setFieldValue("service_id", currentActivity?.service_id?.toString());
            formikRecharge.setFieldValue("cost", currentActivity?.cost?.toString());
        }
    }, [service])

    useEffect(() => {
        setArgumentData(argument)
        if (currentActivity) {
            //TODO: Errore di scrittura della variabile
            formikRecharge.setFieldValue("argument_id", currentActivity?.agrument_id?.toString());
            formikRecharge.setFieldValue("level_id", currentActivity?.level_id?.toString());
        }
    }, [argument])

    useEffect(() => {
        setTopicData(topic)
        if (currentActivity) {
            formikRecharge.setFieldValue("topic_id", currentActivity?.topic_id?.toString());
        }
    }, [topic])

    useEffect(() => {
        setLocationData(location)
        if (currentActivity) {
            formikRecharge.setFieldValue("seat", currentActivity?.seat?.toString());
            let locationRecord = location.find((el) => el.id === currentActivity?.seat?.toString())
            formikRecharge.setFieldValue("room", locationRecord?.room_id);
        } else {
            if (seatId) {
                formikRecharge.setFieldValue("seat", seatId);
                let locationRecord = location.find((el) => el.id === seatId)
                formikRecharge.setFieldValue("room", locationRecord?.room_id);
            }
        }
    }, [location])

    const setChipsStartValue = (date: any) => {
        let oldValue = formikRecharge.values.start_date
        let onlyDate = oldValue.split(" ")[0]
        formikRecharge.setFieldValue("start_date", onlyDate + " " + date)
        formikRecharge.setFieldValue("activity_date", onlyDate + " " + date)
        formikRecharge.setFieldValue("request_date", onlyDate + " " + date)
        const minDateEndLocal = moment(new Date(onlyDate + " " + date)).toDate()
        setMinDateEnd(minDateEndLocal)
    }

    const setChipsEndValue = (date: any) => {
        let oldValue = formikRecharge.values.end_date
        let onlyDate = oldValue.split(" ")[0]
        formikRecharge.setFieldValue("end_date", onlyDate + " " + date)
    }

    return (
        <>
            {
                !loading && !showSuccessResult && (
                    <form onSubmit={formikRecharge.handleSubmit}>
                        <Grid container spacing={4}>
                            <Grid item xs={12}>
                                <Grid container justifyContent="flex-start">
                                    <Autocomplete
                                        id="student_id"
                                        value={
                                            studentData.find((el) => el.id === formikRecharge.values.student_id) ||
                                            null
                                        }
                                        onChange={(event, value) => {
                                            let localMember = member.find((el) => el.id === value?.tutor1);
                                            let localFirm = firm.find((el) => el.id === localMember?.firm_id);
                                            formikRecharge.setFieldValue("firm_id", localFirm?.id);
                                            formikRecharge.setFieldValue("student_id", value?.id);
                                            formikRecharge.setFieldValue("affiliate_id", value?.tutor1);
                                            if (value && value.default_service_id) {
                                                formikRecharge.setFieldValue("service_id", value.default_service_id)
                                                const srv = serviceData.find((el) => el.id === value.default_service_id)
                                                if (srv?.cost)
                                                    formikRecharge.setFieldValue("cost", srv?.cost)
                                            }
                                            if (value && value.default_teacher_id)
                                                formikRecharge.setFieldValue("teacher_id", value.default_teacher_id)
                                        }}
                                        options={studentData}
                                        fullWidth
                                        autoHighlight
                                        disabled={loadingOperation}
                                        getOptionLabel={(option) => option.name + " " + option.surname}
                                        isOptionEqualToValue={(option) => option.id === formikRecharge.values.student_id}
                                        renderOption={(props, option) => (
                                            <Box component="li" sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                 key={option.id}>
                                                {option.name + " " + option.surname}
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={intl.formatMessage({id: "FRM00062"})}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: "one-time-code" // disable autocomplete and autofill
                                                }}
                                                error={formikRecharge.touched.student_id && Boolean(formikRecharge.errors.student_id)}
                                            />
                                        )}
                                    />
                                    {formikRecharge.errors.student_id && formikRecharge.touched.student_id &&
                                        <FormHelperText error>{formikRecharge.errors.student_id}</FormHelperText>}
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider>
                                    <span style={{color: theme.palette.grey[500]}}>Orario</span>
                                </Divider>
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                    <DateTimePicker
                                        InputProps={{
                                            id: "start_date",
                                            name: "start_date",
                                            autoComplete: "one-time-code"
                                        }}
                                        disableMaskedInput={true}
                                        label={"Inizio attività"}
                                        inputFormat={"EE dd-MM-yyyy HH:mm"}
                                        minDateTime={!currentActivity ? minDateStart : undefined}
                                        disabled={loadingOperation}
                                        renderInput={(props) => <TextField fullWidth {...props} />}
                                        value={formikRecharge.values.start_date}
                                        onChange={(value) => {
                                            if (value) {
                                                formikRecharge.setFieldValue("start_date", format(new Date(value), "yyyy-MM-dd HH:mm"))
                                                formikRecharge.setFieldValue("activity_date", format(new Date(value), "yyyy-MM-dd HH:mm"))
                                                formikRecharge.setFieldValue("request_date", format(new Date(value), "yyyy-MM-dd HH:mm"))
                                                const minDateEndLocal = moment(new Date(value)).toDate()
                                                setMinDateEnd(minDateEndLocal)
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <Stack direction={"row"} style={{marginTop: "8px"}} justifyContent={"space-between"}>
                                    <Chip label={"14:00"} size={"small"}
                                          onClick={() => setChipsStartValue("14:00")}/>
                                    <Chip label={"14:30"} size={"small"}
                                          onClick={() => setChipsStartValue("14:30")}/>
                                    <Chip label={"15:00"} size={"small"}
                                          onClick={() => setChipsStartValue("15:00")}/>
                                    <Chip label={"15:30"} size={"small"}
                                          onClick={() => setChipsStartValue("15:30")}/>
                                    <Chip label={"16:00"} size={"small"}
                                          onClick={() => setChipsStartValue("16:00")}/>
                                    <Chip label={"16:30"} size={"small"}
                                          onClick={() => setChipsStartValue("16:30")}/>
                                    <Chip label={"17:00"} size={"small"}
                                          onClick={() => setChipsStartValue("17:00")}/>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                                    <DateTimePicker
                                        InputProps={{
                                            id: "end_date",
                                            name: "end_date",
                                            autoComplete: "one-time-code"
                                        }}
                                        disableMaskedInput={true}
                                        label={"Fine attività"}
                                        inputFormat={"EE dd-MM-yyyy HH:mm"}
                                        minDateTime={minDateEnd}
                                        disabled={loadingOperation}
                                        renderInput={(props) => <TextField fullWidth {...props} />}
                                        value={formikRecharge.values.end_date}
                                        onChange={(value) => {
                                            if (value) {
                                                formikRecharge.setFieldValue("end_date", format(new Date(value), "yyyy-MM-dd HH:mm"))
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <Stack direction={"row"} style={{marginTop: "8px"}} justifyContent={"space-between"}>
                                    <Chip label={"15:00"} size={"small"}
                                          onClick={() => setChipsEndValue("15:00")}/>
                                    <Chip label={"15:30"} size={"small"}
                                          onClick={() => setChipsEndValue("15:30")}/>
                                    <Chip label={"16:00"} size={"small"}
                                          onClick={() => setChipsEndValue("16:00")}/>
                                    <Chip label={"16:30"} size={"small"}
                                          onClick={() => setChipsEndValue("16:30")}/>
                                    <Chip label={"17:00"} size={"small"}
                                          onClick={() => setChipsEndValue("17:00")}/>
                                    <Chip label={"17:30"} size={"small"}
                                          onClick={() => setChipsEndValue("17:30")}/>
                                    <Chip label={"18:00"} size={"small"}
                                          onClick={() => setChipsEndValue("18:00")}/>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider>
                                    <span style={{color: theme.palette.grey[500]}}>Automatici</span>
                                </Divider>
                            </Grid>
                            {
                                user?.type != "4" && (
                                    <Grid item xs={12}>
                                        <Grid container alignItems="center" spacing={1}>
                                            <Grid item xs={12} sm={4}>
                                                <Typography variant="subtitle1"><FormattedMessage
                                                    id={"FRM00014"}/></Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <Grid container justifyContent="flex-start">
                                                    <Autocomplete
                                                        id="teacher_id"
                                                        value={
                                                            teacherData.find((el) => el.id === formikRecharge.values.teacher_id) ||
                                                            null
                                                        }
                                                        onChange={(event, value) => {
                                                            formikRecharge.setFieldValue("teacher_id", value?.id);
                                                        }}
                                                        options={teacherData}
                                                        fullWidth
                                                        autoHighlight
                                                        disabled={loadingOperation}
                                                        getOptionLabel={(option) => option.name + " " + option.surname}
                                                        isOptionEqualToValue={(option) => option.id === formikRecharge.values.teacher_id}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" {...props}
                                                                 key={option.id}>
                                                                {option.name + " " + option.surname}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={intl.formatMessage({id: "FRM00015"})}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: "one-time-code" // disable autocomplete and autofill
                                                                }}
                                                                error={formikRecharge.touched.teacher_id && Boolean(formikRecharge.errors.teacher_id)}
                                                            />
                                                        )}
                                                    />
                                                    {formikRecharge.errors.teacher_id && formikRecharge.touched.teacher_id &&
                                                        <FormHelperText
                                                            error>{formikRecharge.errors.teacher_id}</FormHelperText>}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )
                            }
                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage id={"FRM00064"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container justifyContent="flex-start">
                                            <Autocomplete
                                                id="service_id"
                                                value={
                                                    serviceData.find((el) => el.id === formikRecharge.values.service_id) ||
                                                    null
                                                }
                                                onChange={(event, value) => {
                                                    let topicLocal = topic.find((el) => el.id === value?.topic)
                                                    let argumentLocal = topic.find((el) => el.id === topicLocal?.argument_id)
                                                    formikRecharge.setFieldValue("service_id", value?.id);
                                                    formikRecharge.setFieldValue("cost", value?.cost);
                                                    formikRecharge.setFieldValue("topic_id", value?.topic);
                                                    formikRecharge.setFieldValue("level_id", value?.level);
                                                    formikRecharge.setFieldValue("argument_id", argumentLocal?.id);
                                                    if (value?.topic) {
                                                        setTeacherData(teacher.filter((el) => el.topic_id == "" || el.topic_id == null || el.topic_id == value?.topic))
                                                    } else {
                                                        setTeacherData(teacher)
                                                    }
                                                }}
                                                options={serviceData}
                                                fullWidth
                                                autoHighlight
                                                disabled={loadingOperation}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option) => option.id === formikRecharge.values.service_id}
                                                renderOption={(props, option) => (
                                                    <Box component="li"
                                                         sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                         key={option.id}>
                                                        {option.name}
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={intl.formatMessage({id: "FRM00065"})}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: "one-time-code" // disable autocomplete and autofill
                                                        }}
                                                        error={formikRecharge.touched.service_id && Boolean(formikRecharge.errors.service_id)}
                                                    />
                                                )}
                                            />
                                            {formikRecharge.errors.service_id && formikRecharge.touched.service_id &&
                                                <FormHelperText error>{formikRecharge.errors.service_id}</FormHelperText>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage id={"ACC00017"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container justifyContent="flex-start">
                                            <Autocomplete
                                                id="firm_id"
                                                value={
                                                    firmData.find((el) => el.id === formikRecharge.values.firm_id) ||
                                                    null
                                                }
                                                onChange={(event, value) => {
                                                    formikRecharge.setFieldValue("firm_id", value?.id);
                                                }}
                                                options={firmData}
                                                fullWidth
                                                autoHighlight
                                                disabled={loadingOperation}
                                                getOptionLabel={(option) => option.name + ""}
                                                renderOption={(props, option) => (
                                                    <Box component="li"
                                                         sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                         key={option.id}>
                                                        {option.name}
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={intl.formatMessage({id: "ACC00018"})}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: "one-time-code" // disable autocomplete and autofill
                                                        }}
                                                        error={formikRecharge.touched.firm_id && Boolean(formikRecharge.errors.firm_id)}
                                                    />
                                                )}
                                            />
                                            {formikRecharge.errors.firm_id && formikRecharge.touched.firm_id &&
                                                <FormHelperText error>{formikRecharge.errors.firm_id}</FormHelperText>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider>
                                    <span style={{color: theme.palette.grey[500]}}>Opzionali</span>
                                </Divider>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage
                                            id={"FRM00093"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container justifyContent="flex-start">
                                            <Autocomplete
                                                id="argument_id"
                                                value={argumentData.find((el) => el.id === formikRecharge.values.argument_id) ||
                                                    null}
                                                onChange={(event, value) => {
                                                    formikRecharge.setFieldValue("argument_id", value?.id);
                                                }}
                                                options={argumentData}
                                                fullWidth
                                                autoHighlight
                                                disabled={loadingOperation}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option) => option.id === formikRecharge.values.argument_id}
                                                renderOption={(props, option) => (
                                                    <Box component="li"
                                                         sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                         key={option.id}>
                                                        {option.name}
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={intl.formatMessage({id: "FRM00090"})}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: "one-time-code" // disable autocomplete and autofill
                                                        }}
                                                        error={formikRecharge.touched.argument_id && Boolean(formikRecharge.errors.argument_id)}
                                                    />
                                                )}
                                            />
                                            {formikRecharge.errors.argument_id && formikRecharge.touched.argument_id &&
                                                <FormHelperText
                                                    error>{formikRecharge.errors.argument_id}</FormHelperText>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage id={"FRM00094"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container justifyContent="flex-start">
                                            <Autocomplete
                                                id="topic_id"
                                                value={
                                                    topicData.find((el) => el.id === formikRecharge.values.topic_id) ||
                                                    null
                                                }
                                                onChange={(event, value) => {
                                                    formikRecharge.setFieldValue("topic_id", value?.id);
                                                }}
                                                options={topicData}
                                                fullWidth
                                                autoHighlight
                                                disabled={loadingOperation}
                                                getOptionLabel={(option) => option.name}
                                                isOptionEqualToValue={(option) => option.id === formikRecharge.values.topic_id}
                                                renderOption={(props, option) => (
                                                    <Box component="li"
                                                         sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                         key={option.id}>
                                                        {option.name}
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={intl.formatMessage({id: "FRM00011"})}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: "one-time-code" // disable autocomplete and autofill
                                                        }}
                                                        error={formikRecharge.touched.topic_id && Boolean(formikRecharge.errors.topic_id)}
                                                    />
                                                )}
                                            />
                                            {formikRecharge.errors.topic_id && formikRecharge.touched.topic_id &&
                                                <FormHelperText error>{formikRecharge.errors.topic_id}</FormHelperText>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage id={"FRM00012"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <FormControl>
                                            <RadioGroup
                                                row
                                                aria-label="color"
                                                value={formikRecharge.values.level_id}
                                                defaultValue={"1"}
                                                onChange={formikRecharge.handleChange}
                                                name="level_id"
                                                id="level_id"
                                            >
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio color="secondary" sx={{color: "secondary.main"}}/>}
                                                    label="Low"
                                                    disabled={loadingOperation}
                                                />
                                                <FormControlLabel
                                                    value="2"
                                                    control={<Radio color="primary" sx={{color: "primary.main"}}/>}
                                                    label="Medium"
                                                    disabled={loadingOperation}
                                                />
                                                <FormControlLabel
                                                    value="3"
                                                    control={<Radio color="error" sx={{color: "error.main"}}/>}
                                                    label="High"
                                                    disabled={loadingOperation}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1"><FormattedMessage id={"FRM00066"}/></Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <Grid container justifyContent="flex-start">
                                            <Autocomplete
                                                id="seat"
                                                value={
                                                    locationData.find((el) => el.id === formikRecharge.values.seat) ||
                                                    null
                                                }
                                                onChange={(event, value) => {
                                                    formikRecharge.setFieldValue("seat", value?.id);
                                                    formikRecharge.setFieldValue("room", value?.room_id);
                                                }}
                                                groupBy={(option) => option.location_name!}
                                                options={[...locationData].reverse()}
                                                fullWidth
                                                autoHighlight
                                                disabled={loadingOperation}
                                                getOptionLabel={(option) => option.seat_code + " - " + option.room_name + " - " + option.location_name}
                                                isOptionEqualToValue={(option) => option.id === formikRecharge.values.seat}
                                                renderOption={(props, option) => (
                                                    <Box component="li"
                                                         sx={{"& > img": {mr: 2, flexShrink: 0}}} {...props}
                                                         key={option.id}>
                                                        {option.seat_code} - {option.room_name} - {option.location_name}
                                                    </Box>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={intl.formatMessage({id: "FRM00067"})}
                                                        inputProps={{
                                                            ...params.inputProps,
                                                            autoComplete: "one-time-code" // disable autocomplete and autofill
                                                        }}
                                                        error={formikRecharge.touched.seat && Boolean(formikRecharge.errors.seat)}
                                                    />
                                                )}
                                            />
                                            {formikRecharge.errors.seat && formikRecharge.touched.seat &&
                                                <FormHelperText error>{formikRecharge.errors.seat}</FormHelperText>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Divider/>
                            </Grid>
                            <Grid item xs={12} md={6} mt={3}>

                            </Grid>
                            <Grid item xs={12} md={6} mt={3}>
                                <Stack direction={"row"} textAlign={"right"} spacing={3}>
                                    <AnimateButton>
                                        <Button fullWidth variant="text" color={"error"} onClick={handleDrawerChangeState}
                                                disabled={loadingOperation}>
                                            <FormattedMessage id={"SYS00003"}/>
                                        </Button>
                                    </AnimateButton>
                                    <AnimateButton>
                                        <LoadingButton fullWidth variant="contained" type="submit"
                                                       loading={loadingOperation || loading}>
                                            {
                                                currentActivity && (
                                                    <FormattedMessage id={"TAB00018"}/>
                                                )
                                            }
                                            {
                                                !currentActivity && (
                                                    <FormattedMessage id={"FRM00007"}/>
                                                )
                                            }
                                        </LoadingButton>
                                    </AnimateButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                )
            }
            {
                loading && (
                    <LoadingSpinner/>
                )
            }
            {
                showSuccessResult && (
                    <SuccessResult setSuccessResult={setSuccessResult} handleDrawerChangeState={handleDrawerChangeState}/>
                )
            }
            <ModalAction
                open={openModal}
                title={intl.formatMessage({id: "SYS00011"})}
                content={intl.formatMessage({id: "ACC00027"})}
                buttonContent={intl.formatMessage({id: "FRM00018"})}
                handleClose={handleCloseModal}
                handleAction={handleConfirm}/>

            <ModalWarningActivity
                open={showWarningDialog}
                checkResult={resultCheck}
                handleClose={handleCloseWarningModal}
                handleAction={handleConfirmAnyway}/>
        </>
    );
};
export default ActivityForm;